import { useEffect, useState, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { getRaffles, drawRaffle, resetRaffle, RaffleState } from './api';

interface Props {
  streamerId: number;
  animationId: number;
  mode?: string | null;
}

export default function RafflePanel({ streamerId, animationId, mode }: Props) {
  const [state, setState] = useState<RaffleState | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const res = await getRaffles(streamerId);
    setState(res.data.find(r => r.config.theme_animation_id === animationId || (r.config as any).id === animationId) ?? null);
  }, [streamerId, animationId]);

  useEffect(() => {
    load().catch(() => {});
    const t = setInterval(() => load().catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (kind: 'gift_random' | 'top_donor' | 'reset') => {
    setBusy(true); setMsg('');
    try {
      if (kind === 'reset') {
        if (!window.confirm('Archiver le concours actuel et repartir à zéro ?')) return;
        await resetRaffle(streamerId, animationId);
        setMsg('Nouveau concours prêt');
      } else {
        await drawRaffle(streamerId, animationId, kind);
        setMsg(kind === 'top_donor' ? 'Meilleur donateur affiché' : 'Tirage lancé');
      }
      await load();
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || 'Action impossible');
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(''), 3500);
    }
  };

  const entries = state?.entries ?? [];
  const isTop = mode === 'top_donor';
  const ordered = [...entries].sort((a, b) => isTop ? ((b.coins || 0) - (a.coins || 0) || (b.entries_count || 0) - (a.entries_count || 0)) : String(b.last_seen_at || '').localeCompare(String(a.last_seen_at || '')));

  return (
    <div style={{ background: '#151c2b', border: '1px solid #2f3b52', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cbd5e1', fontSize: 11, fontWeight: 800 }}>
        <span>{isTop ? 'Top donateurs' : 'Participants'}</span>
        <span>{state?.total_participants ?? 0} / {state?.total_entries ?? 0}</span>
      </div>
      <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {ordered.slice(0, 20).map((e, i) => (
          <div key={e.username} style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, color: '#e2e8f0', fontSize: 11 }}>
            <span style={{ color: '#64748b', width: 16 }}>{i + 1}</span>
            {e.avatar_url ? <img src={e.avatar_url} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} /> : <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#312e81', flexShrink: 0 }} />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{e.nickname || e.username}</span>
            <span style={{ color: isTop ? '#38bdf8' : '#a78bfa', fontWeight: 800 }}>{isTop ? `🪙 ${e.coins || 0}` : `${e.entries_count || 0}x`}</span>
          </div>
        ))}
        {ordered.length === 0 && <div style={{ color: '#64748b', fontSize: 11, padding: 8, textAlign: 'center' }}>En attente du cadeau de participation</div>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isTop ? '1fr 1fr' : '1fr', gap: 6 }}>
        <button disabled={busy || ordered.length === 0} onClick={() => act(isTop ? 'top_donor' : 'gift_random')} style={btn('#7c3aed')}>
          {isTop ? '🏆 Valider top' : '🎲 Tirer au sort'}
        </button>
        {isTop && <button disabled={busy || ordered.length === 0} onClick={() => act('gift_random')} style={btn('#2563eb')}>🎁 Mystère</button>}
      </div>
      <button disabled={busy} onClick={() => act('reset')} style={ghost}>Nouveau concours</button>
      {msg && <div style={{ color: msg.includes('impossible') ? '#f87171' : '#22c55e', fontSize: 11 }}>{msg}</div>}
    </div>
  );
}

const btn = (bg: string): CSSProperties => ({ background: bg, border: 'none', color: '#fff', borderRadius: 7, padding: '8px 6px', fontSize: 11, fontWeight: 800, cursor: 'pointer' });
const ghost: CSSProperties = { background: 'transparent', border: '1px solid #374151', color: '#94a3b8', borderRadius: 7, padding: '7px 6px', fontSize: 11, cursor: 'pointer' };
